import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ImportDateRange,
  getDefaultImportDateRange,
  getImportDateRangeError,
} from '../utils/importDateRange';

const STORAGE_KEY = 'importDateRange.v1';
const EVENT_NAME = 'import-date-range-change';

const readStoredRange = (): ImportDateRange => {
  if (typeof window === 'undefined') {
    return getDefaultImportDateRange();
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return getDefaultImportDateRange();
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        startDate: typeof parsed.startDate === 'string' ? parsed.startDate : getDefaultImportDateRange().startDate,
        endDate: typeof parsed.endDate === 'string' ? parsed.endDate : getDefaultImportDateRange().endDate,
      };
    }
  } catch {
    // ignore parse errors and fall back to defaults
  }
  return getDefaultImportDateRange();
};

export const useSharedImportDateRange = () => {
  const [range, setRangeState] = useState<ImportDateRange>(() => readStoredRange());

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
      }
    } catch {
      // ignore storage errors
    }
  }, [range]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handler = (event: Event) => {
      if (event.type !== EVENT_NAME) return;
      const detail = (event as CustomEvent<ImportDateRange>).detail;
      if (!detail) return;
      setRangeState((prev) => {
        if (prev.startDate === detail.startDate && prev.endDate === detail.endDate) {
          return prev;
        }
        return detail;
      });
    };
    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  const setRange = useCallback((next: ImportDateRange) => {
    setRangeState(next);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<ImportDateRange>(EVENT_NAME, { detail: next }));
    }
  }, []);

  const error = useMemo(() => getImportDateRangeError(range), [range]);

  return { range, setRange, error };
};
